
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  Box,
  Button,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { formatCurrency } from "../../hooks/Currency";
import { DropLi } from "../../hooks";

import ReportPrintButton from "../reports/ReportPrintButton";

//------------------------------------------------
// Reusable Sale Details Content
//------------------------------------------------
const SaleDetailsContent = ({
  viewData,
  store,
  payTypes,
  t,
}) => {
  const sellItems = Array.isArray(viewData?.sellItems)
    ? viewData.sellItems
    : [];

  const customerCreditUsed =
    Number(viewData?.customerCreditUsed) || 0;

  const cashAmount =
    Number(viewData?.cashAmount) || 0;

  const cardAmount =
    Number(viewData?.cardAmount) || 0;

  const money = (value) =>
    formatCurrency(Number(value) || 0, store);

  //------------------------------------------------
  // Cashier
  //------------------------------------------------
  const cashierName =
    [
      viewData?.user?.firstName,
      viewData?.user?.lastName,
    ]
      .filter(Boolean)
      .join(" ") ||
    viewData?.username ||
    "";

  //------------------------------------------------
  // Payment Type
  //------------------------------------------------
  const getPaymentType = () => {
    const paymentMethods = [];

    if (customerCreditUsed > 0) {
      paymentMethods.push(t("customerCredit"));
    }

    if (cashAmount > 0) {
      paymentMethods.push(t("cash"));
    }

    if (cardAmount > 0) {
      paymentMethods.push(t("card"));
    }

    if (paymentMethods.length > 0) {
      return paymentMethods.join(" + ");
    }

    const paymentType = payTypes.find(
      (item) =>
        String(item.id) ===
        String(viewData?.paymentType)
    );

    return (
      paymentType?.name ||
      viewData?.paymentType ||
      "-"
    );
  };

  //------------------------------------------------
  // Total Items
  //------------------------------------------------
  const totalItems =
    viewData?.totalItem ??
    sellItems.filter((item) => item.itemType === 1).length;

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <Box>
      {/* Sale Details Title */}
      <Box sx={{ textAlign: "center", mt: 2, mb: 3 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold" }}
        >
          {t("saleDetails")}
        </Typography>

        <Box
          sx={{
            width: "80%",
            borderBottom: "1px solid #ccc",
            mx: "auto",
            mt: 2,
          }}
        />
      </Box>

      {/* Sold Products */}
      <TableContainer sx={{ mt: 2 }}>
        <Table size="small" aria-label="sale items">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("itemName")}
              </TableCell>

              <TableCell
                align="right"
                sx={{ fontWeight: "bold" }}
              >
                {t("quantity")}
              </TableCell>

              <TableCell
                align="right"
                sx={{ fontWeight: "bold" }}
              >
                {t("price")}
              </TableCell>

              <TableCell
                align="right"
                sx={{ fontWeight: "bold" }}
              >
                {t("total")}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sellItems.map((item, index) => {
              const quantity = Number(item.qty) || 0;
              const price = Number(item.price) || 0;

              return (
                <TableRow key={item.id ?? index}>
                  <TableCell>
                    {item.itemType === 1
                      ? item.product?.name || ""
                      : t("discount")}
                  </TableCell>

                  <TableCell align="right">
                    {quantity}
                  </TableCell>

                  <TableCell align="right">
                    {money(price)}
                  </TableCell>

                  <TableCell align="right">
                    {money(quantity * price)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 2 }} />

      {/* Sale Summary */}
      <Grid container spacing={2}>
        {/* Left Column */}
        <Grid item xs={12} sm={6}>
          <TableContainer>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>
                    {t("transactionDate")}
                  </TableCell>

                  <TableCell align="right">
                    {viewData?.sellDate || ""}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("paymentType")}
                  </TableCell>

                  <TableCell align="right">
                    {getPaymentType()}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("cashier")}
                  </TableCell>

                  <TableCell align="right">
                    {cashierName}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("customer")}
                  </TableCell>

                  <TableCell align="right">
                    {viewData?.customer?.customerName ||
                      t("walkInCustomer")}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} sm={6}>
          <TableContainer>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>
                    {t("totalItem")}
                  </TableCell>

                  <TableCell align="right">
                    {totalItems}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("subtotal")}
                  </TableCell>

                  <TableCell align="right">
                    {money(viewData?.subtotal)}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("tax")} (
                    {viewData?.taxPct || 0}%)
                  </TableCell>

                  <TableCell align="right">
                    {money(viewData?.tax)}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold" }}
                  >
                    {t("totalPay")}
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{ fontWeight: "bold" }}
                  >
                    {money(viewData?.totalPay)}
                  </TableCell>
                </TableRow>

                {/* Customer Credit Used */}
                {customerCreditUsed > 0 && (
                  <TableRow>
                    <TableCell>
                      {t("customerCreditUsed")}
                    </TableCell>

                    <TableCell align="right">
                      {money(customerCreditUsed)}
                    </TableCell>
                  </TableRow>
                )}

                {/* Cash Amount */}
                {cashAmount > 0 && (
                  <TableRow>
                    <TableCell>
                      {t("cash")}
                    </TableCell>

                    <TableCell align="right">
                      {money(cashAmount)}
                    </TableCell>
                  </TableRow>
                )}

                {/* Card Amount */}
                {cardAmount > 0 && (
                  <TableRow>
                    <TableCell>
                      {t("card")}
                    </TableCell>

                    <TableCell align="right">
                      {money(cardAmount)}
                    </TableCell>
                  </TableRow>
                )}

                <TableRow>
                  <TableCell>
                    {t("paymentReceived")}
                  </TableCell>

                  <TableCell align="right">
                    {money(viewData?.received)}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("change")}
                  </TableCell>

                  <TableCell align="right">
                    {money(viewData?.change)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

//------------------------------------------------
// View Sale Details
//------------------------------------------------
const ViewProd = (props) => {
  const { t } = useTranslation();

  const { setShowForm, viewData } = props;

  const _store = useSelector(
    (state) => state.pos.defStore
  );

  const { PayType } = DropLi();

  const reportRef = useRef(null);

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <Grid container className="p-20">
      {/* Print Preview Button */}
      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 1,
        }}
      >
        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("saleDetails")}
        />
      </Grid>

      {/* Screen Sale Details */}
      <Grid item xs={12}>
        <SaleDetailsContent
          viewData={viewData}
          store={_store}
          payTypes={PayType || []}
          t={t}
        />
      </Grid>

      {/* Print-only Sale Details */}
      <Grid item xs={12}>
        <Box
          ref={reportRef}
          className="sale-details-print"
          sx={{
            position: "absolute",
            left: "-10000px",
            top: 0,
            width: "1000px",
            backgroundColor: "#fff",
            color: "#000",
            p: 2,
          }}
        >
          <SaleDetailsContent
            viewData={viewData}
            store={_store}
            payTypes={PayType || []}
            t={t}
          />
        </Box>
      </Grid>

      {/* Close Button */}
      <Grid
        item
        xs={12}
        className="tx-center mt-40"
      >
        <Button
          onClick={() => setShowForm(false)}
          className="btn-error"
          size="large"
          sx={{ minWidth: "150px" }}
        >
          {t("close")}
        </Button>
      </Grid>
    </Grid>
  );
};

export default ViewProd;
